import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, glyphs, useMediaQuery } from "../../Styles";
import { Overlay, ProgressCircle, NewModal, LineInfoDisplay, FieldMode, Last10DaysViewer, UNIT, MainContentPaddingTypes, FeedCurveWithLabels } from "..";
import { ButtonTypes, ListStyle_Types, SelectFieldOption_Values } from "../../Definitions";
import { UndefinedOptions, useFeedcurveOptions } from "../_Components";
import { Graph_QL, useAnimal, MaintainAnimal, useFeedCurves, AnimalInfo } from "../../Services";
import { debounce } from "lodash";
import store, { useAppDispatch, useAppSelector } from "../../Store";
import { selectAnimalById } from "../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/AnimalDataSlices/animalsSlice";
import { selectFeedCurveById } from "../../Services/Agrisys_GqlDataCaller/FeedDataCalls/FeedDataSlices/feedCurvesSlice";
import { addActiveAnimals, clearActiveAnimals, resetActiveAnimals, selectActiveAnimalById, updateActiveAnimal } from "../../Store/activeStateHandlers/activeAnimalSlice";
import { Dispatch, UnknownAction } from "redux";
import NewButton, { NewButtonList } from "../BaseComponents/newButton";

interface Feed_Props {
  selectedAnimal: number;
  connect: {
    domain: string;
    connection: Graph_QL.Connection;
  }
}

const debounceAnimalUpdate = debounce((key: keyof AnimalInfo, value: number, animal: AnimalInfo, dispatch: Dispatch<UnknownAction>) => {
  dispatch(updateActiveAnimal({id: animal.animal_id, changes: {...animal, [key]: value }}));
}, 500)
/**
 * Feed
 * @param selectedAnimal - The ID of the selected animal.
 * @param connect - Connection and domain information for data fetching.
 * @returns
 */
export default function Feed({ selectedAnimal, connect }: Feed_Props) {
  const { t } = useTranslation();
  const feedcurveOptions = useFeedcurveOptions(UndefinedOptions.undefined)
  const dirtRef = useRef<MaintainAnimal>({ animal_id: selectedAnimal });
  const [showFeedViewModal, setFeedViewModal] = useState<boolean>();
  const [feedViewContent, setFeedViewContent] = useState<"FEEDPLAN" | "LASTDAYS">("FEEDPLAN");
  const [editMode, setEditMode] = useState<boolean>(false);
  const [feed, setFeed] = useState<number | undefined>(undefined);
  const { maintainEntries: updateAnimals } = useAnimal(connect.connection);
  const { data: feedCurvesState } = useFeedCurves(connect.connection)
  const dispatch = useAppDispatch();
  
  // #region | Mediaqueries
  const mobile = useMediaQuery(`(max-width: 650px), (min-width: 1060px) and (max-width: 1390px), (min-width: 741px) and (max-width: 1000px)`);
  const mobileXs = useMediaQuery(`(max-width: 450px), (min-width: 1060px) and (max-width: 1150px)`);
  // #endregion

  // Update animal_id when selectedAnimal changes
  useEffect(() => {
    dirtRef.current.animal_id = selectedAnimal;
    dispatch(clearActiveAnimals())
  }, [dispatch, selectedAnimal]);

  const animal = useAppSelector((state) => selectAnimalById(state.graphQlData.animals, selectedAnimal));
  
  // #region | Data
  // Get animal from selectedAnimal
  const thisAnimal = useAppSelector((state) => {return selectActiveAnimalById(state.activeState.activeAnimal, selectedAnimal)}) 


  useEffect(() => {
    if (animal) {
      dispatch(addActiveAnimals([animal]))
    }
  },[animal, dispatch])

  const animalFeedCurve = useAppSelector((state) => {if (thisAnimal?.feedcurve_id) {return selectFeedCurveById(state.graphQlData.feedCurves, thisAnimal.feedcurve_id)}} );
  // Get animal info from thisAnimal
  const animalInfo = useMemo(() => {
    if (!thisAnimal) return;

    // Feed Correction
    const feedCorrection = thisAnimal.correction === undefined || thisAnimal.correction === null
      ? "0%"
      : `${thisAnimal.correction}%`;
    
    // Feed Consumptions
    const feedConsumptions = thisAnimal.consumptions
    const lastTenDays = thisAnimal.lastTenDay

    // Consumption today and yesterday
    const consumptionToday = thisAnimal.consumptions ? thisAnimal.consumptions.find(e => e.milestoneDayOffset!==undefined && e.milestoneDayOffset === 0):undefined;
    const consumptionTotalToday = +((consumptionToday?.total ?? 0) / 1000).toFixed(2);
    const consumptionProgressedToday = +(consumptionTotalToday - (consumptionToday?.progressed ?? 0) / 1000).toFixed(2);
    const consumptionYesterday = thisAnimal.consumptions ? thisAnimal.consumptions.find(e => e.milestoneDayOffset!==undefined && e.milestoneDayOffset === 1):undefined;
    const consumptionTotalYesterday = +((consumptionYesterday?.total ?? 0) / 1000).toFixed(2);
    const consumptionProgressedYesterday = +(consumptionTotalYesterday - (consumptionYesterday?.progressed ?? 0) / 1000).toFixed(2);
  
    // Milestone
    const milestone = thisAnimal.milestone
     const milestone_day = thisAnimal.milestone_day

    return {
      consumptionTotalToday,
      consumptionTotalYesterday,
      consumptionProgressedToday,
      consumptionProgressedYesterday,
      feedCorrection,
      feedCurve: animalFeedCurve,
      feedConsumptions,
      lastTenDays,
      milestone,
      milestone_day
    };
  }, [animalFeedCurve, thisAnimal]);
  // #endregion

  const milestoneFromFeedcurves = useAppSelector((state) => {if (thisAnimal?.feedcurve_id) {return selectFeedCurveById(state.graphQlData.feedCurves, thisAnimal.feedcurve_id)}})?.milestones ?? [];

  const milestoneOptionss = milestoneFromFeedcurves.map((m, index) => {
    return ({
      id: m.feedCurveMilestoneId,
      label: m.name
    }) as SelectFieldOption_Values
  })
  
  // Set feed correction
  useEffect(() => {
    setFeed(thisAnimal?.correction)
  }, [thisAnimal?.correction]);

  // #region | Line Displays
  // Feed Plan
  const info_feedPlan = (
    <LineInfoDisplay
      editMode={editMode}
      gridTemplateColumns={"7fr 25px 8fr"}
      value={thisAnimal?.feedCurve?.name}
      undefinedValue={t("negativeParams.feedCurve", { defaultValue: "No Feed Curve" })}
      header={t("feed.feedCurve.feedCurve", { defaultValue: "Feed Curve" })}
      icon={"FeedCurve"}
      fieldMode={FieldMode.select}
      options={feedcurveOptions.feedcurveOptions ?? []}
      onchange={(option: SelectFieldOption_Values | undefined) => {
        dirtRef.current.feedcurve_id = option?.id ? Number(option?.id): NaN
        if (isNaN(Number(option?.id))) {
          dispatch(updateActiveAnimal({id: thisAnimal.animal_id, changes: {...thisAnimal, feedcurve_id: Number(option?.id)}}))
          return;
        }

        const feedCurve = selectFeedCurveById(store.getState().graphQlData.feedCurves, Number(option?.id)) ?? undefined;
        if (feedCurve === undefined) {
          dispatch(updateActiveAnimal({id: thisAnimal.animal_id, changes: {...thisAnimal, feedcurve_id: Number(option?.id)}}))
          return;
        }
         
        const milestone = feedCurve.milestones?.find((milestone) => milestone.milestoneId === thisAnimal.milestone?.milestone_id);
        if (milestone !== undefined) {
          dispatch(updateActiveAnimal({id: thisAnimal.animal_id, changes: {...thisAnimal, feedcurve_id: Number(option?.id), feedcurve_milestones: milestone.feedCurveMilestoneId}}))
          dirtRef.current.fk_feedcurve_milestones = milestone.feedCurveMilestoneId
          if (thisAnimal.milestone_day) {
            dirtRef.current.milestone_day = Math.floor(thisAnimal.milestone_day/1000);
          }
          return;
        }

        const newMilestone = feedCurve.milestones?.at(0);
        dispatch(updateActiveAnimal({id: thisAnimal.animal_id, changes: {...thisAnimal, feedcurve_id: Number(option?.id), feedcurve_milestones: newMilestone?.feedCurveMilestoneId, milestone: newMilestone ? {milestone_id: newMilestone.milestoneId, name: newMilestone.name} : newMilestone, milestone_day: Date.now()}}))
        dirtRef.current.fk_feedcurve_milestones = newMilestone ? newMilestone.feedCurveMilestoneId : NaN
        dirtRef.current.milestone_day = Math.floor(Date.now()/1000);
      }}
      defaultValue={feedcurveOptions.feedcurveOptions.find(({ id }) => thisAnimal&&thisAnimal.feedCurve?.feedCurveId === id) ?? undefined}
      isClearable
    />
  );

  // Feed Correction
  const info_corrections = (
    <LineInfoDisplay
      editMode={editMode}
      gridTemplateColumns={"7fr 25px 8fr"}
      value={animalInfo ? animalInfo.feedCorrection ?? "0%" : "0%"}
      header={t("feed.feedCorrection.feedCorrection", { defaultValue: "Feed Corrections" })}
      icon={"FeedCorrection"}
      fieldMode={FieldMode.feedCorrection}
      onChange={(e: number) => { dirtRef.current.correction = Number(e); setFeed(e); debounceAnimalUpdate("correction", Number(e), thisAnimal, dispatch) }}
      defaultValue={feed}
    />
  );

  // Milestone
  const info_milestone = (
    <LineInfoDisplay
      editMode={editMode}
      gridTemplateColumns={"7fr 25px 8fr"}
      value={thisAnimal ? thisAnimal.milestone?.name : ""}
      header={t("feed.feedCorrection.milestone", { defaultValue: "Milestone" })}
      undefinedValue={t("neagtiveParams.milestone", { defaultValue: "No Milestone" })}
      icon={"Milestone"}
      fieldMode={FieldMode.select}
      options={milestoneOptionss ?? []}
      onchange={(option: SelectFieldOption_Values | undefined) => {(dirtRef.current.fk_feedcurve_milestones = option?.id ? Number(option.id) : NaN); dispatch(updateActiveAnimal({id: thisAnimal.animal_id, changes: {...thisAnimal, feedcurve_milestones: Number(option?.id)}}))}}
      defaultValue={milestoneOptionss.find(({id}) => thisAnimal&&thisAnimal.feedcurve_milestones === id) ?? undefined}
      isClearable
    />
  );

  // Milestone
  const info_milestone_day = (
    <LineInfoDisplay 
      gridTemplateColumns={"7fr 25px 8fr"}
      editMode={editMode}
      value={thisAnimal?.milestone_day ? Math.floor((Date.now() - thisAnimal?.milestone_day) / (1000 * 60 * 60 * 24)) : undefined}
      header={t("params.milestoneDay", { defaultValue: "Milestone Day" })}
      undefinedValue={t("negativeParams.milestoneDay", { defaultValue: "No Milestone Day" })}
      icon={"MilestoneDay"}
      fieldMode={FieldMode.input}
      onBlur={() => {
        const animal = dirtRef.current
        if (animal && animal.milestone_day && animal.milestone_day !== thisAnimal.milestone_day) {
          dispatch(updateActiveAnimal({id: animal.animal_id, changes: {...animal, milestone_day: animal.milestone_day*1000}}))}
        }}
      onchange={(e: string|number) => (dirtRef.current.milestone_day = Math.round((Date.now() - Number(e) * 86400000) / 1000) ? Math.round((Date.now() - Number(e) * 86400000) / 1000) : NaN)}
      defaultValue={thisAnimal?.milestone_day ? Math.floor((Date.now() - thisAnimal?.milestone_day) / (1000 * 60 * 60 * 24)) : undefined}
      isClearable
      type='number'
    />
  );
  // #endregion

  // #region | Feed graphs
  // Feed Graphs Buttons
  const feedGraphsBtns = (
    <NewButtonList
      contents={[
        t("feed.feedCurve.feedCurve", { defaultValue: "feed curve" }),
        t("feed.general.feedLastDays", { defaultValue: "last 10 days" }),
      ]}
      overrides={{ baseStyles: { width: "fit-content" } }}
      onClick={(i) => {
        if (i === 0) { setFeedViewModal(true); setFeedViewContent("FEEDPLAN") }
        if (i === 1) { setFeedViewModal(true); setFeedViewContent("LASTDAYS") }
      }}
      listOverrides={{ listStyle: ListStyle_Types.horizontal }}
    />
  );

  // Feed Curve Graph
  const feedCurve = ( <>
    {animalFeedCurve ? (
      <FeedCurveWithLabels data={animalFeedCurve} />
    ) : (
      <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}> {t("feed.feedCurve.noFeedCurve", { defaultValue: "No Feed Curve Found" })} </p>
    )}
  </>);
  
  // Last 10 Days Graph
  const lastDays = (<>
    {animalInfo&&animalInfo.lastTenDays ? (
      <div style={{ marginTop: "20px", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "20px" }}>
        <Last10DaysViewer data={animalInfo.lastTenDays} />
      </div>
    ) : (
      <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}> {t("feed.feedCurve.noConsumptions", { defaultValue: "No Consumptions Data Found" })} </p>
    )} </>);
  
  // Feed View Modal
  const feedGraphsModals = (
    <NewModal
      modalState={!!showFeedViewModal}
      overrides={{
        backgroundColor: colors.greyLightTertiary,
        mainContentScroll: true,
        mainContentPadding: MainContentPaddingTypes.primary
      }}
      tabButtons={{
        contents: [
          t("feed.feedCurve.feedCurve", { defaultValue: "feed curve" }),
          t("feed.general.feedLastDays", { defaultValue: "last 10 days" })
        ],
        onClick: (i) => {
          if (i === 0) { setFeedViewContent("FEEDPLAN") }
          if (i === 1) { setFeedViewContent("LASTDAYS") }
        },
        startEnhancers: [glyphs.FeedCurve({}), glyphs.FeedPlan({})],
        disabled: feedViewContent === "FEEDPLAN" ? 0 : 1,
        active: feedViewContent === "FEEDPLAN" ? 0 : 1
      }}
      predefinedButtons={{ onClose: () => { setFeedViewModal(false) }}}
      header={ feedViewContent === "FEEDPLAN"
        ? t("feed.feedCurve.feedCurve", { defaultValue: "feed curve" })
        : t("feed.general.feedLastDays", { defaultValue: "last 10 days" })
      }
      subHeader={`${t("animal.animalId", { defaultValue: "animal no." })} ${thisAnimal?.animal_number}`}
      mainContent={
        <div style={{display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px"}}>
          { feedViewContent === "FEEDPLAN"
            ? <>{feedCurve}</>
            : <>{lastDays}</>
          }
        </div>
      }
    />
  );
  // #endregion

  return ( <>
    {editMode ? <Overlay onClickAlert beforeUnload /> : <></>}
    <div style={{ position: "relative", height: "calc(100% - 50px)",  zIndex: editMode ? "9999" : "0"  }}>
      <div style={{ display: "flex", position: "relative", justifyContent: "end", padding: "0px 20px"}}>
        {editMode ? (
          <NewButtonList
            contents={[
              t("buttons.save", { defaultValue: "save" }),
              t("buttons.cancel", { defaultValue: "cancel" }),
              t("buttons.edit", { defaultValue: "edit" }),
            ]}
            buttonType={ButtonTypes.tableButton}
            startEnhancers={[
              glyphs.Check({}),
              glyphs.Close({}),
              glyphs.Edit({}),
            ]}
            onClick={(i) => {
              if (i === 0) { setEditMode(false); if (updateAnimals) { updateAnimals({ data: [dirtRef.current] })} dirtRef.current = { animal_id: selectedAnimal } }
              if (i === 1) { setEditMode(false); dirtRef.current = { animal_id: selectedAnimal }; dispatch(resetActiveAnimals([animal])) }
            }}
            disabled={[false, false, true]}
            overrides={(index) => ({
              baseStyles: { backgroundColor: index === 2 ? colors.greyPrimary : colors.greySecondary },
              hoverStyles: { backgroundColor: colors.greyPrimary },
            })}
          />
        ) : (
          <NewButton
            content={t("buttons.edit", { defaultValue: "edit" })}
            buttonType={ButtonTypes.tableButton}
            startEnhancer={glyphs.Edit({})}
            onClick={() => setEditMode(true)}
          />
        )}
      </div>
      <div
        style={{
          backgroundColor: colors.greyLightTertiary,
          border: "5px solid " + colors.greyPrimary,
          borderRadius: "10px",
          display: "grid",
          gridTemplateColumns: mobile ? "1fr" : "1fr 1fr",
          gridTemplateRows: mobile ? "1fr 1fr" : "1fr",
          height: "100%",
          boxSizing: "border-box",
          overflowY: mobile ? "scroll" : "hidden",
          scrollbarColor: colors.greySecondary + colors.greyPrimary,
          scrollbarWidth: "thin",
        }}
      >
        <div style={{ gridArea: "1/1/2/2", padding: "10px", display: "flex", alignItems: "start", justifyContent: "center" }}>
          <div style={{ ...css.modalTop, width: "fit-content", alignItems: "end", gap: mobileXs ? "40px" : "60px" }}>
            {/* Fed Today */}
            <ProgressCircle
              animation
              header={t("feed.general.remainingToday", { defaultValue: "remaining today" })}
              total={animalInfo?.consumptionTotalToday}
              progressed={animalInfo?.consumptionProgressedToday}
              unit={<UNIT content={"Kg"} color={colors.greyPrimary} glyph={<glyphs.Kg size="15px" fillColor={colors.greyPrimary}/>}/>} 
              tooMuchFedDisclaimer
            />
            {/* Fed During Period */}
            <ProgressCircle
              animation
              header={t("feed.general.remainingYesterday", { defaultValue: "remaining yesterday" })}
              total={animalInfo?.consumptionTotalYesterday}
              progressed={animalInfo?.consumptionProgressedYesterday}
              unit={<UNIT content={"Kg"} color={colors.greyPrimary} glyph={<glyphs.Kg size="15px" fillColor={colors.greyPrimary}/>}/>} 
              tooMuchFedDisclaimer
            />
          </div>
        </div>
        <div style={{
          gridArea: mobile ? "2/1/3/2" : "1/2/2/3",
          padding: "10px",
          display: "flex",
          gap: "20px",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: colors.greyPrimary,
        }}>
          <div style={{
            zIndex: editMode ? "9999" : "0",
            width: "270px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
          }}>
            {info_feedPlan}
            {info_corrections}
            {info_milestone}
            {info_milestone_day}
          </div>
          <div style={{ width: "80%", margin: "0 auto" }}>
            <div style={{ height: "1px", backgroundColor: colors.greySecondary, marginBottom: "20px" }}/>
            <div style={{ display: "flex", justifyContent: "center" }}>
              {feedGraphsBtns}
            </div>
            {feedGraphsModals}
          </div>
        </div>
      </div>
    </div>
  </> );
}
